import { api } from '../../core/network/api';

export type ArtifactRarity = 'COMMON' | 'UNCOMMON' | 'RARE' | 'EPIC' | 'LEGENDARY' | 'MYTHIC';
export type ArtifactType = 'WEAPON' | 'HELMET' | 'ARMOR' | 'BOOTS' | 'RING' | 'AMULET';
export type EquipSlot = 'weapon' | 'helmet' | 'armor' | 'boots' | 'ring' | 'amulet';
/** An item's NATIVE currency — what its prices are denominated in and what it sells back for. */
export type ArtifactCurrency = 'SOFT' | 'PREMIUM';

/** Display order of the 6 equip slots. */
export const SLOTS: EquipSlot[] = ['weapon', 'helmet', 'armor', 'boots', 'ring', 'amulet'];

export interface Artifact {
  id: string;
  name: string;
  description: string | null;
  rarity: ArtifactRarity;
  type: ArtifactType;
  attack: number;
  defense: number;
  magic: number;
  speed: number;
  hp: number;
  /** The grade the item was born at (uncapped) — see effectiveLevel in item-math. */
  powerLevel: number;
  /** The 1..MAX_ARTIFACT_LEVEL sharpening track. */
  level: number;
  setName: string | null;
  statusEffect: string | null;
  /** Drifting street price + its base anchor — the API sends full artifact rows;
   *  the sell-payout preview (ItemModal) reads these. Both are denominated in
   *  `currency`: silver for SOFT gear, runes for rune-bought gear. Never convert. */
  currentPrice: number;
  basePrice: number;
  currency: ArtifactCurrency;
  imageUrl: string | null;
  isBreakable: boolean;
  maxDurability: number | null;
  durability: number | null;
  isBroken: boolean;
}

export interface ArtifactBonuses {
  attack: number;
  defense: number;
  magic: number;
  speed: number;
  hp: number;
}

export interface Inventory {
  equipped: Record<EquipSlot, Artifact | null>;
  bag: Artifact[];
  bonuses: ArtifactBonuses;
  activeSets: string[];
  /** Bag capacity — how full the sea-chest is / its current cap (Sea-Chest Expansion raises the latter). */
  bagCount: number;
  bagCap: number;
}

// Routes live under /api/gear (the core owns /api/inventory — a separate UserItem
// stack system). This is the equipment/loadout system: 6 equip slots + artifacts.

/** GET /api/gear — equipped slots + bag + aggregate bonuses + active sets. */
export const fetchInventory = () => api.get<Inventory>('/gear');

/** POST /api/gear/equip — equip an owned artifact into its matching slot. */
export const equipArtifact = (artifactId: string) =>
  api.post<Inventory>('/gear/equip', { artifactId });

/** POST /api/gear/unequip — clear a slot (item returns to the bag). */
export const unequipSlot = (slot: EquipSlot) =>
  api.post<Inventory>('/gear/unequip', { slot });

export interface RepairResult {
  repaired: string;
  cost: number;
  inventory: Inventory;
}

/** POST /api/gear/repair — repair a worn/broken breakable item (debits cash). */
export const repairArtifact = (artifactId: string) =>
  api.post<RepairResult>('/gear/repair', { artifactId });

export interface UpgradeResult { upgraded: string; level: number; cost: number; inventory: Inventory }

/** POST /api/gear/upgrade — upgrade an owned artifact one level (1→5), debits cash. */
export const upgradeArtifact = (artifactId: string) =>
  api.post<UpgradeResult>('/gear/upgrade', { artifactId });
